import {VNode} from 'snabbdom';
import {jsx} from 'panel';

import {
  ReportVisualization,
  ReportVisualizationAttrs,
  ReportVisualizationProps,
  VisualizationEventHandlers,
} from 'common/report/report-manager/visualization';
import {RetentionBookmarkParams} from 'common/report/retention/util/types';
import {SuccessResponse} from 'common/report/retention/models/types';
import {ChartType, FeatureGate, ReportName} from 'common/report/constants';
import {ChartHeaderUpdateDetail} from 'common/widgets/chart-header';
import {BookmarkUpdateDetail, RequestOdaDetail, RequestOdaEvent} from 'common/report/report-manager/types';
import {ORDERED_TIME_UNITS, RetentionQuery} from 'common/report/retention/models/retention-query';
import {
  generateRetentionBucketSectionItems,
  getFormattedRowData,
  hasRetentionData,
} from 'common/report/retention/util/parsing';
import {flattenQueryResponse} from 'common/report/retention/util/request';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {ChartHeaderAttrs} from 'common/widgets/chart-header/types';
import {SelectTheme} from 'common/components/select/constants';
import {SelectEl} from 'common/components/select';
import {Section} from 'common/components/items-menu/types';
import {ChangeEvent} from 'common/components/select/types';
import {TimeUnitLabels} from 'common/widgets/date-range-picker/models/types';
import {SortChangeEvent, TrackName as DataTableTrackName} from 'common/components/data-table/types';
import {SegmentFilterDataTableJsxProps} from 'common/charts/misc/segment-filter-data-table';
import {ODAControlEl} from 'common/widgets/oda-control';
import {BaseQueryErrorType} from 'common/report/types';
import {QueryReason} from 'common/report/util/query-tracking/types';
import {useReportConsistency} from 'common/report/util/feature-sampling';

import {chartHeaderToRetentionTypes, getRetentionChartHeaderAttrs} from './util';
import {RetentionChartDisplayEl} from './chart-display';
import {RetentionTableEl} from './table';

export type RetentionVisualizationJsxProps = {
  attrs: ReportVisualizationAttrs;
  props: ReportVisualizationProps<RetentionBookmarkParams, SuccessResponse, BaseQueryErrorType>;
  on: VisualizationEventHandlers;
};

export class RetentionVisualization extends ReportVisualization<
  RetentionBookmarkParams,
  SuccessResponse,
  BaseQueryErrorType
> {
  reportName = ReportName.Retention;

  bookmarkUpdateFromHeaderUpdate(update: ChartHeaderUpdateDetail): BookmarkUpdateDetail<RetentionBookmarkParams> {
    const query = this.getRetentionQuery();
    const queryUpdates = chartHeaderToRetentionTypes(update, query);
    const newQuery = query.createFromUpdate(queryUpdates);
    const params = newQuery.generateBookmark();

    return {
      params,
      queryReason: QueryReason.QbOtherUpdate,
      requery: !newQuery.queryEquals(query),
      hasChangedChartType: this.bookmarkParams.chart_type !== params.chart_type,
    };
  }

  reportSpecificHeaderAttrs(): Partial<ChartHeaderAttrs> {
    const query = this.getRetentionQuery();
    const flattenedInfo = this.results && flattenQueryResponse(this.results, query.groupBy);
    return getRetentionChartHeaderAttrs(query, flattenedInfo?.segmentKeyToPath, this.attr(`workspace-id`) || null);
  }

  headerChildrenTemplate(): VNode {
    return (
      <>
        {this.renderBucketSelector()}
        {this.renderDateUnitSelector()}
      </>
    );
  }

  chartDisplay(): VNode {
    const query = this.getRetentionQuery();
    return (
      <RetentionChartDisplayEl
        attrs={{
          'is-download-mode': this.attr(`is-download-mode`),
          'show-resizing-layout': this.attr(`show-resizing-layout`),
          elref: `retentionChartDisplay`,
        }}
        props={{
          project: this.project,
          queryAndResults: {
            query,
            results: this.results,
          },
        }}
      />
    );
  }

  // override data table template for custom retention table
  dataTableTemplate(): VNode {
    const query = this.getRetentionQuery();
    const {metadata, data} = flattenQueryResponse(this.results, query.groupBy);
    const tableData = getFormattedRowData(data, metadata.incomplete_buckets, query.groupBy, this.project?.timezone);
    const tableHasData = hasRetentionData(tableData);

    if (
      tableHasData &&
      (query.chartType === ChartType.RetentionTrendMetric || query.chartType === ChartType.RetentionTrend)
    ) {
      return super.dataTableTemplate();
    } else if (query.isRetentionCurveBasedChart()) {
      const rowLimit = this.attr(`is-download-mode`) ? 30 : 255;
      const rowData = tableData.slice(0, rowLimit);

      return (
        <RetentionTableEl
          sel='.retention-curve-table'
          attrs={{
            'project-id': this.attr(`project-id`),
            'project-timezone': this.project?.timezone,
            'resizing-layout': this.attr(`show-resizing-layout`),
            elref: `curveTable`,
          }}
          props={{
            rowData,
            query,
            queryResults: this.results,
          }}
          on={{
            sortChange: (ev: SortChangeEvent) => {
              this.updateBookmarkParamsFromModel({sorting: ev.detail.sorting});
            },
            changeSortColumn: ({detail: {column}}) => {
              this.updateBookmarkParamsFromModel({
                sortColumn: column,
              });
            },
            changeSortDesc: ({detail: {desc}}) => {
              this.updateBookmarkParamsFromModel({
                sortDesc: desc,
              });
            },
            update: (ev) => {
              if (Object.prototype.hasOwnProperty.call(ev.detail, `expandedSegments`)) {
                const {expandedSegments} = ev.detail;
                this.updateBookmarkParamsFromModel({expandedSegments});
              }
            },
          }}
        />
      );
    }

    return null;
  }

  getBookmarkParamsFromSortChange(ev: SortChangeEvent): RetentionBookmarkParams {
    return {...this.bookmarkParams, sorting: ev.detail.sorting};
  }

  reportSpecificDataTableAttrs(): SegmentFilterDataTableJsxProps['attrs'] {
    let trackName: DataTableTrackName = null;
    const query = this.getRetentionQuery();

    switch (query.chartType) {
      case ChartType.RetentionTrendMetric: {
        trackName = DataTableTrackName.RetentionTrendsMetric;
        break;
      }
      case ChartType.RetentionTrend: {
        trackName = DataTableTrackName.RetentionTrends;
        break;
      }
    }

    return {'track-name': trackName};
  }

  dataTableFooterTemplate() {
    const query = this.getRetentionQuery();
    const {metadata, data} = flattenQueryResponse(this.results, query.groupBy);
    const tableData = getFormattedRowData(data, metadata.incomplete_buckets, query.groupBy, this.project?.timezone);
    const tableHasData = hasRetentionData(tableData);

    const showOnDemandAnalysisFooter =
      tableHasData &&
      query.retentionType !== `addiction` &&
      !query.isSegmentedByCohort() &&
      !!this.project?.getFeatureGate(FeatureGate.CanSegmentRetention) &&
      !this.attr(`is-download-mode`);
    return (
      showOnDemandAnalysisFooter && (
        <ODAControlEl
          sel='.oda-control'
          attrs={{
            description: `Receive an email of statistically significant segments impacting retention.`,
            'show-upsell': !this.project?.getFeatureGate(FeatureGate.OnDemandAnalysis),
            'upsell-description': `Rather than searching through multiple property and cohort breakdowns to find significant data, receive an email of statistically significant segments with higher or lower retention.`,
          }}
          on={{
            clickAnalyze: () => {
              const detail: RequestOdaDetail = {
                bookmarkDateRange: query.dateRange,
                bookmarkUrl: window.location.hash.substr(1), // strip the '#' sign
              };

              const event: RequestOdaEvent = new CustomEvent(`request-oda`, {detail});
              this.dispatchEvent(event);
            },
          }}
        />
      )
    );
  }

  private renderBucketSelector(): null | VNode {
    const query = this.getRetentionQuery();
    const showBucketSelector =
      (query.chartType === ChartType.RetentionTrend || query.chartType === ChartType.RetentionTrendMetric) &&
      !useReportConsistency();
    if (showBucketSelector) {
      const items = generateRetentionBucketSectionItems({
        query,
        results: this.results,
        timezone: this.project?.timezone,
      });

      return (
        <SelectEl
          sel='.bucket-selector'
          key={`bucket-selector`}
          attrs={{
            sections: [{items}],
            'menu-width': 120,
            theme: SelectTheme.Secondary,
            slot: `chart-controls`,
          }}
          on={{
            change: (ev: ChangeEvent) => {
              this.updateBookmarkParamsFromModel({
                retentionBucket: ev.detail.item.value,
              });
            },
          }}
        />
      );
    }

    return null;
  }

  private renderDateUnitSelector(): null | VNode {
    const query = this.getRetentionQuery();
    const sectionItems: Section['items'] = ORDERED_TIME_UNITS.map((unit) => ({
      value: unit,
      label: TimeUnitLabels[unit],
      isSelected: unit === query.dateUnit,
    }));

    return (
      <SelectEl
        sel='.date-unit-selector'
        key={`date-unit-selector`}
        attrs={{
          sections: [{items: sectionItems}],
          theme: SelectTheme.Secondary,
          'menu-width': 120,
          slot: `date-unit-selector`,
        }}
        on={{
          change: (ev) => {
            const {value} = ev.detail.item;
            this.updateBookmarkParamsFromModel(query.createFromDateUnitUpdate(value));
          },
        }}
      />
    );
  }

  private getRetentionQuery(): RetentionQuery {
    return RetentionQuery.createFromBookmark(this.bookmarkParams);
  }

  private updateBookmarkParamsFromModel(
    queryUpdates: Partial<RetentionQuery>,
    queryReason = QueryReason.QbOtherUpdate,
  ): void {
    const query = this.getRetentionQuery();
    const newQuery = query.createFromUpdate(queryUpdates);
    this.dispatchUpdate({
      params: newQuery.generateBookmark(),
      queryReason,
      requery: !newQuery.queryEquals(query),
    });
  }
}

export const RetentionVisualizationEl = defineAndMakeJsxFactory<RetentionVisualizationJsxProps>(
  `retention-visualization`,
  RetentionVisualization,
);
export const RetentionVisualizationReact = panel2react<RetentionVisualizationJsxProps>(
  `retention-visualization`,
  RetentionVisualization,
);
